/// <reference path="../groupthink-js.d.ts" />

import { fetcher, apiRequest } from '../lib';
import { useTask } from './task';
import { useAgenda } from './agenda';
import { useDocument } from './document';
import { useGuestRoom } from './room';
import { ThreadableContext } from './thread';
import useSWR from 'swr';
import { useCallback, useMemo } from 'react';
export const useUser = (id: string) => {
  // TODO: The type for useSWR would be <Groupthink.SuccessfulResponseContent<'user.me'>> IFF id==='me'
  const {
    data: user,
    error,
    isLoading,
    mutate,
  } = useSWR(`/v1/users/${id}`, fetcher, {
    onSuccess: (data) => {
      // if we have a window object and a dataLayer
      // @ts-ignore
      if (typeof window !== 'undefined' && Boolean(window.dataLayer)) {
        // @ts-ignore
        window.dataLayer.push(
          {
            event: 'userLoaded',
          },
          {
            user_id: data.data.id,
          }
        );
      }
    },
  });

  const updateUser = useCallback(
    async <RouteName = 'user.updateMe'>({
      setErrors,
      setIsUpdating,
      onSuccess,
      payload,
    }: Omit<Groupthink.UpdateOperationOptions<RouteName>, 'payload'> & {
      payload?: Groupthink.RequestPayload<RouteName> & { avatar?: string };
    }) => {
      const { ...jsonPayload } = payload;
      // If this is not a string, then we need to upload the avatar.
      if (payload?.avatar && !(typeof payload.avatar === 'string')) {
        // We need to use FormData and multipart/form-data to upload the avatar.
        // But if we use multipart/form-data, we can't send JSON in the same request.
        // To work around this, we send the JSON in a field called "payload".
        // The backend will parse the JSON and use it to update the user.

        const formData = new FormData();
        formData.append('avatar', payload.avatar);
        formData.append('payload', JSON.stringify(jsonPayload));

        return apiRequest<RouteName>(`/v1/users/me`, mutate, 'POST', {
          setErrors,
          setLoading: setIsUpdating,
          payload: formData,
          onSuccess,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        return apiRequest<RouteName>(`/v1/users/me`, mutate, 'PUT', {
          setErrors,
          setLoading: setIsUpdating,
          payload,
          onSuccess,
        });
      }
    },
    [mutate]
  );

  const deleteUser = useCallback(
    async <RouteName = 'user.delete'>({
      setErrors,
      setIsDeleting,
      onSuccess,
    }: Groupthink.DeleteOperationOptions<RouteName>) =>
      apiRequest<RouteName>(`/v1/users/me`, mutate, 'DELETE', {
        setErrors,
        setLoading: setIsDeleting,
        onSuccess,
      }),
    [mutate]
  );

  return useMemo(
    () => ({
      user: user?.data,
      isLoading,
      isError: error,
      mutate,
      updateUser,
      deleteUser,
    }),
    [user?.data, isLoading, error]
  );
};

export const useUserEligibility = () => {
  const {
    data: user,
    error,
    isLoading,
    mutate,
  } = useSWR<Groupthink.SuccessfulResponseContent<'user.teamInitiatorEligibility'>>(
    `/v1/users/me/eligibility`,
    fetcher
  );

  return {
    eligibility: user?.data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useOrganizationCollaborators = (workspaceId: string) => {
  const {
    data: user,
    error,
    isLoading,
    mutate: mutateList,
  } = useSWR(
    workspaceId ? `/v1/workspaces/` + workspaceId + `/users/collaborators` : null,
    fetcher
  );

  return {
    users: user?.data,
    isLoading,
    isError: error,
    mutateList,
  };
};

// Agendas ordered by most recent meeting
export const useAgendasInCommon = (userId: string) => {
  const {
    data: agendas,
    error,
    isLoading,
    mutate: mutateList,
  } = useSWR(userId ? `/v1/users/${userId}/agendasInCommon` : null, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  });

  return {
    agendas: agendas?.data,
    isLoading,
    isError: error,
    mutateList,
  };
};

export const useOrganizationUsers = (workspaceId: string) => {
  const {
    data: user,
    error,
    isLoading,
    mutate: mutateList,
  } = useSWR<Groupthink.SuccessfulResponseContent<'user.index'>>(
    workspaceId ? `/v1/workspaces/` + workspaceId + `/users` : null,
    fetcher
  );

  const createUser = async <RouteName = 'user.create'>({
    setErrors,
    setIsCreating,
    onSuccess,
    payload,
  }: Groupthink.CreateOperationOptions<RouteName>) =>
    apiRequest<RouteName>(`/v1/workspaces/${workspaceId}/users`, mutateList, 'POST', {
      setErrors,
      setLoading: setIsCreating,
      payload,
      onSuccess,
    });

  const updateUser = async <RouteName = 'user.updateWorkspaceUser'>(
    id: string,
    { setErrors, setIsUpdating, onSuccess, payload }: Groupthink.UpdateOperationOptions<RouteName>
  ) =>
    apiRequest<RouteName>(`/v1/workspaces/${workspaceId}/users/${id}`, mutateList, 'PUT', {
      setErrors,
      setLoading: setIsUpdating,
      payload,
      onSuccess,
    });

  const deleteUser = async <RouteName = 'user.remove'>(
    id: string,
    { setErrors, setIsDeleting, onSuccess }: Groupthink.DeleteOperationOptions<RouteName>
  ) =>
    apiRequest<RouteName>(`/v1/workspaces/${workspaceId}/users/${id}`, mutateList, 'DELETE', {
      setErrors,
      setLoading: setIsDeleting,
      onSuccess,
    });

  return {
    users: user?.data,
    isLoading,
    isError: error,
    mutateList,
    createUser,
    updateUser,
    deleteUser,
  };
};

export const usePublicUser = (id: string) => {
  const { data: user, error, isLoading, mutate } = useSWR(id && `/v1/public/users/${id}`, fetcher);

  return {
    user: user?.data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const usePublicUserContributions = (id: string) => {
  const {
    data: contributions,
    error,
    isLoading,
    mutate,
  } = useSWR(id && `/v1/public/users/${id}/contributions`, fetcher);

  return {
    contributions: contributions?.data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const looseEmailValidator = (email) => {
  const re = /^\S+@\S+\.\S+$/;
  return re.test(email);
};

export const selectOnboardingPercentComplete = (user: Groupthink.SelfUserResource) => {
  if (!user?.onboarding_status) return 100;

  const completeSteps = Object.values(user.onboarding_status ?? []).filter(Boolean).length;
  return Math.round((completeSteps / Object.keys(user.onboarding_status).length) * 100);
};

// The react-mentions library only needs an id and a display name. We pass additional metadata to be used by our custom suggestion renderer.
export interface UserSuggestion {
  id: number;
  display: string;
  avatar: string;
  username?: string;
  email?: string;
}

const GROUPTHINK_BOT_SUGGESTION_DATA = {
  id: 0,
  display: 'Groupthink',
  username: 'groupthink',
  avatar: '/gt_avatar.png',
};

type ThreadableResource =
  | Groupthink.AgendaResource
  | Groupthink.DocumentResource
  | Groupthink.RoomResource;

/**
 * Hook to get user suggestions for @mentions in a threadable context.
 *
 * Returns users from the given threadable (agenda/document/room) formatted for react-mentions.
 *
 * @example
 * ```tsx
 * const suggestions = useSuggestedUsers(ThreadableContext.Agenda, "123");
 * // Returns: [{ id: 1, display: "John Doe", avatar: "url" }, ...]
 * ```
 */
export const useSuggestedUsers = (
  /**
   * The threadable context (Agenda/Document/Room)
   */
  context: ThreadableContext,

  /**
   * ID of the threadable to fetch users from
   */
  threadableId: string | undefined,

  options?: {
    /**
     * When set, do not include the Groupthink bot as a suggestion
     */
    excludeGroupthink?: boolean;
  },

  organizationId?: string
): UserSuggestion[] => {
  const { excludeGroupthink = false } = options ?? {};

  // We cannot selectively call just one out of the three useAgenda/useDocument/useGuestRoom hooks, as that would violate the rule-of-hooks.
  // These hooks are no-ops when given a null agendaId/documentId/roomId, so by only setting the appropiate id based on the threadableId context, we ensure only one network request is sent.
  const agendaId = context === ThreadableContext.Agenda ? threadableId : undefined;
  const documentId = context === ThreadableContext.Document ? threadableId : undefined;
  const roomId = context === ThreadableContext.Room ? threadableId : undefined;
  const taskId = context === ThreadableContext.Task ? threadableId : undefined;

  const { agenda } = useAgenda(agendaId);
  const { room } = useGuestRoom(roomId);
  const { document } = useDocument(documentId);
  // @ts-ignore
  const { task } = useTask(organizationId, taskId);

  let threadable: ThreadableResource | undefined;
  switch (context) {
    case ThreadableContext.Agenda:
      threadable = agenda as Groupthink.AgendaResource;
      break;
    case ThreadableContext.Document:
      threadable = document as Groupthink.DocumentResource;
      break;
    case ThreadableContext.Room:
      threadable = room as Groupthink.RoomResource;
      break;
    case ThreadableContext.Task:
      threadable = task;
      break;

    /**
     * This will flag any unexpected context as a TypeScript error.
     */
    default: {
      const _exhaustiveCheck: never = context;
      throw new Error(`Unhandled threadable context: ${JSON.stringify(_exhaustiveCheck)}`);
    }
  }

  // Early exit if we have not yet loaded the threadable or can't load the users on the threadable
  if (!threadable?.users) {
    return excludeGroupthink ? [] : [GROUPTHINK_BOT_SUGGESTION_DATA];
  }

  // Transform our users into something that react-mentions understands, which is `{ id, display}`. We'll use the avatar and username to extend the suggestions popup.
  const suggestions =
    threadable?.users?.map((user) => {
      // Use the display name by default, falling back to username, finally email.
      const display = user.name ?? user.username ?? user.email;

      return {
        id: user.id,
        display,
        avatar: user.avatar,
        username: user.user_name,
        email: user.email,
      };
    }) ?? [];

  if (excludeGroupthink) {
    return suggestions;
  }

  return [...suggestions, GROUPTHINK_BOT_SUGGESTION_DATA];
};

export const getSubstringIndex = (str: string, substr: string, ignoreAccents: boolean = false) => {
  if (!ignoreAccents) {
    return str.toLowerCase().indexOf(substr.toLowerCase());
  }

  const normalizedStr = str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const normalizedSubstr = substr
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return normalizedStr.indexOf(normalizedSubstr);
};
